import { z } from 'zod'
import { IsoTimestamp } from '../primitives.js'
import { Venue } from '../enums.js'

/** `live`: agents may be created and trade on it. `coming_soon`: listed and teased with a waitlist ("Notify me"); writes refuse 409 `VENUE_NOT_AVAILABLE`. */
export const VenueStatus = z.enum(['live', 'coming_soon'])
export type VenueStatus = z.output<typeof VenueStatus>

/** Icon key the web maps to a mark (`VenueMark`); never a URL. */
export const VenueIcon = z.enum(['robinhood-chain', 'hyperliquid', 'solana'])
export type VenueIcon = z.output<typeof VenueIcon>

/** One entry of the venue catalog (`@algoboo/schema/catalog` `VENUES`). */
export const VenueInfo = z.object({
  id: Venue,
  name: z.string(),
  /** The compact label (the top-bar switcher on a phone, badges). */
  shortName: z.string().max(16),
  /** EVM chain id the venue settles on; null when it has none (Solana). */
  chainId: z.number().int().positive().nullable(),
  /** The network readout: `Chain 4663`, `HyperEVM 999`, `Solana mainnet-beta`. */
  network: z.string(),
  status: VenueStatus,
  description: z.string(),
  /** The one-line tagline of the picker card and the switcher (coming-soon venues: what is on the way). */
  teaser: z.string(),
  icon: VenueIcon,
  /** Display order (ascending); `VENUES` is sorted by it. */
  order: z.number().int().nonnegative(),
})
export type VenueInfo = z.output<typeof VenueInfo>

/**
 * GET /venues row: the catalog entry, the public number of people waiting for it (`waitlistCount`, 0 for a live
 * venue) and the caller's waitlist state (`viewer` null without a session).
 */
export const VenueListing = VenueInfo.extend({
  waitlistCount: z.number().int().nonnegative().default(0),
  viewer: z.object({ waitlisted: z.boolean() }).nullable(),
})
export type VenueListing = z.output<typeof VenueListing>

export const VenueParams = z.object({ id: Venue })
export type VenueParams = z.input<typeof VenueParams>

/** POST /venues/:id/waitlist: idempotent per user and venue; `joinedAt` is the first sign-up. */
export const VenueWaitlistResult = z.object({
  venue: Venue,
  waitlisted: z.literal(true),
  joinedAt: IsoTimestamp,
})
export type VenueWaitlistResult = z.output<typeof VenueWaitlistResult>

/** DELETE /venues/:id/waitlist: idempotent; the caller's own row only (a repeat, or no row, answers the same). */
export const VenueWaitlistLeft = z.object({
  venue: Venue,
  waitlisted: z.literal(false),
})
export type VenueWaitlistLeft = z.output<typeof VenueWaitlistLeft>
